import { useEffect, useState } from "react";
import Toaster, { ToastConfig } from "../../components/Toaster";
import { ErrorTypes } from "../../interfaces/errors";
import {
  formatDate,
  formatDateTask,
  formatDateTime,
} from "../../helper/dateFormatter";
import { FilterCriteria } from "tasks-experience-client";
import {
  CellClickedEvent,
  ColDef,
  ValueFormatterParams,
  ValueGetterParams,
} from "ag-grid-community";
import { GenericGrid } from "../../grid/GenericGrid";

import { createEligiblesApi } from "../../api-helpers/PatientApiHelper";

import { ReactComponent as EditIcon } from "../../assets/img/EditPencil.svg";
import { Modal } from "react-bootstrap";
import {
  EligiblesOutreachAttemptsDto,
  GridDataRequest,
  GridDataResponse,
} from "patients-experience-client";
import PhoneCellRenderer from "../../renderers/PhoneCellRenderer";
import { EligibilityHeader } from "./EligibilityHeader";
import OffCanvas from "../../components/offCanvas";
import { EditEligiblePatient } from "./EditEligiblePatient";
import {
  WorkflowStateCellValueGetter,
  workflowStatesCache,
} from "../../grid/renderers/WorkflowStateCellValueGetter";
import {
  DesignerIdConstants,
  WorkflowConstants,
} from "../../helper/WorkflowConstants";
import { UserName } from "../../components/UserName";
import AddPatient from "../patients/AddPatient";
import "../../assets/scss/custom/_custom.scss";
import { createUsersApi } from "../../api-helpers/UserApiHelper";
import { useNavigate } from "react-router-dom";

export interface Props {
  patientId?: string;
  breadcrumbs?: boolean | undefined;
}

export interface EligiblePatient {
  id: string;
  patientExternalIdFromPartner: string;
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: Date;
  phoneNumber: string | null;
  phoneNumber2: string | null;
  email: string | null;
  genderId: string | null;
  zipCode: string | null;
  contractId: string | null;
  marketId: string | null;
  marketName: string | null;
  onboarded: boolean;
  eligibleDate: string | null;
  pcpFirstName: string | null;
  pcpLastName: string | null;
  assignedUserId: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  cityId: string | null;
  stateId: string | null;
  name: string | null;
  pcpName: string | null;
  partnerName: string | null;
  reachDate: string | null;
  lastOutReachDate: string | null;
  currentStatus: string | null;
  numberOfAttempts: string | number;
  patientId: string | null;
  pathwayDesignerStateId: string | null;
}

export const EligibilityGrid = ({ patientId, breadcrumbs }: Props) => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<FilterCriteria[]>([]);
  const [isUserCacheLoaded, setIsUserCacheLoaded] = useState(false);
  const [initialFiltersReady, setInitialFiltersReady] = useState(false);
  const [selectedEligible, setSelectedEligible] = useState<EligiblePatient>(
    {} as EligiblePatient,
  );
  const [comments, setComments] = useState<EligiblesOutreachAttemptsDto[]>([]);
  const [gridReloadKey, setGridReloadKey] = useState(0);

  const [isEnrollPatient, setIsEnrollPatient] = useState(false);
  const [commentsModalShow, setCommentsModalShow] = useState(false);
  const [offCanvasShow, setOffCanvasShow] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState<ToastConfig>({
    type: "success" as ErrorTypes,
    title: "",
    icon: "",
  });

  const userCache = new Map<string, string>();
  const eligiblesApi = createEligiblesApi();

  const preloadUsers = async () => {
    const usersApi = createUsersApi();
    try {
      const response = await usersApi.usersControllerFindAll();
      response.data.forEach(
        (user: { id: string; firstName: string; lastName: string }) => {
          userCache.set(user.id, `${user.firstName} ${user.lastName}`);
        },
      );
      setIsUserCacheLoaded(true);
    } catch (error) {
      console.error("Failed to preload users:", error);
    }
  };

  const loadComments = async (eligibleId: string) => {
    if (eligibleId === "") return;

    try {
      const { data } =
        await eligiblesApi.eligiblesControllerGetCarePlanDetails(eligibleId);
      setComments(data);
      setCommentsModalShow(true);
    } catch (error) {
      console.error("Failed to fetch comments");
    }
  };

  const reloadGrid = () => {
    setGridReloadKey(gridReloadKey + 1);
  };

  const handleEnrollPatient = async (eligibleId: string) => {
    setIsEnrollPatient(true);
  };

  const handleViewEligibleForm = (event: CellClickedEvent) => {
    setOffCanvasShow(true);
    setSelectedEligible(event.data);
  };

  const showToaster = async (toasterConfig: ToastConfig) => {
    setToastConfig(toasterConfig as ToastConfig);
    setShowToast(true);
  };

  useEffect(() => {
    if (initialFiltersReady) {
      return;
    }

    setInitialFiltersReady(true);
  }, [filters]);

  useEffect(() => {
    setIsEnrollPatient(false);
  }, [offCanvasShow]);

  useEffect(() => {
    if (!isUserCacheLoaded) {
      preloadUsers();
    }
  }, [isUserCacheLoaded]);

  const EditButton = (event: CellClickedEvent) => {
    return (
      <div
        className="d-flex gap-8 align-items-center justify-content-center h-100 cursor-pointer"
        onClick={() => handleViewEligibleForm(event)}
      >
        <EditIcon />
      </div>
    );
  };

  const CommentsRenderer = (params: CellClickedEvent | ValueGetterParams) => {
    return (
      <div className="d-flex align-items-center justify-content-center h-100 gap-2">
        <span className="w-10">{params.data[params.colDef.field!]}</span>
        <span
          className={`material-symbols-outlined fs-20 me-1 align-text-top ${
            params.data[params.colDef.field!] > 0
              ? "visible cursor-pointer"
              : "invisible"
          }`}
          onClick={() => loadComments(params?.data?.id || "")}
        >
          comment
        </span>
      </div>
    );
  };

  const [colDefs, setColDefs] = useState<ColDef[]>([
    {
      headerName: "Patient",
      field: "name",
      minWidth: 150,
      pinned: "left",
      sortable: true,
      cellRenderer: (params: ValueGetterParams) => {
        const { patientId, name } = params.data;

        if (!patientId) {
          return name;
        }

        return (
          <a
            href={`/patients/${patientId}/summary`}
            className="a-link a-underline text-secondary"
            onClick={(e) => {
              e.preventDefault();
              navigate(`/patients/${patientId}/summary`);
            }}
          >
            {name}
          </a>
        );
      },
    },
    {
      headerName: "Phone",
      field: "phoneNumber",
      minWidth: 150,
      width: 150,
      pinned: "left",
      cellClass: "cursor-pointer",
      cellRenderer: PhoneCellRenderer,
    },
    {
      headerName: "Phone 2",
      field: "phoneNumber2",
      minWidth: 150,
      width: 150,
      pinned: "left",
      cellClass: "cursor-pointer",
      cellRenderer: PhoneCellRenderer,
    },
    {
      headerName: "Partner",
      field: "partnerName",
      minWidth: 200,
      pinned: "left",
      sortable: true,
      valueFormatter: (params) => params?.value || "-",
    },
    {
      headerName: "Market",
      field: "marketName",
      minWidth: 180,
      sortable: true,
      valueFormatter: (params) => params?.value || "-",
    },
    {
      headerName: "Pathway",
      field: "pathwayDesignerStateId",
      minWidth: 150,
      sortable: true,
      valueGetter: (params) =>
        WorkflowStateCellValueGetter(DesignerIdConstants.PATHWAY, params),
    },
    {
      headerName: "PCP",
      field: "pcpName",
      minWidth: 120,
      sortable: true,
      valueFormatter: (params) => params?.value || "-",
    },
    {
      headerName: "Assignee",
      field: "assignedUserId",
      minWidth: 120,
      width: 150,
      sortable: true,
      valueFormatter: (params) => userCache.get(params?.value) || "-",
    },
    {
      headerName: "Eligible Date",
      field: "eligibleDate",
      minWidth: 120,
      maxWidth: 150,
      sortable: true,
      valueFormatter: (params) => {
        return formatDateTask(params?.value || "");
      },
    },
    {
      headerName: "Reach Date",
      field: "reachDate",
      minWidth: 120,
      maxWidth: 150,
      sortable: true,
      valueFormatter: (params) => {
        return formatDate(params?.value || "");
      },
    },
    {
      headerName: "Last Outreach Date",
      field: "lastOutReachDate",
      minWidth: 120,
      maxWidth: 180,
      sortable: true,
      cellRenderer: (params: ValueFormatterParams) => {
        const threeDaysAgo = new Date();
        threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
        return (
          <div>
            {formatDate(params?.value || "")}
            {params?.value && new Date(params.value) < threeDaysAgo && (
              <span className="badge background-cancelled ms-2">3+ days</span>
            )}
          </div>
        );
      },
    },
    {
      headerName: "Current Status",
      field: "currentStatus",
      minWidth: 150,
      valueGetter: (params) =>
        WorkflowStateCellValueGetter(DesignerIdConstants.ELIGIBILITY, params),
    },
    {
      headerName: "No. of Attempts",
      field: "numberOfAttempts",
      minWidth: 150,
      maxWidth: 300,
      sortable: true,
      cellRenderer: CommentsRenderer,
    },
    {
      minWidth: 50,
      maxWidth: 50,
      pinned: "right",
      sortable: false,
      cellRenderer: EditButton,
    },
  ]);

  const loadData = async (
    request: GridDataRequest,
  ): Promise<GridDataResponse> => {
    const { data } = await eligiblesApi.eligiblesControllerGetGridData(request);
    return data;
  };

  return (
    <>
      <div className="row">
        <Toaster
          show={showToast}
          onClose={() => setShowToast(false)}
          type={toastConfig.type}
          title={toastConfig.title}
          message={toastConfig.message}
          icon={toastConfig.icon}
        />
      </div>

      <main className="main">
        <EligibilityHeader
          breadcrumbs={breadcrumbs}
          patientId={patientId}
          reloadGrid={() => setGridReloadKey((prev) => prev + 1)}
          onUpdateFilters={setFilters}
        />
        <div className="d-flex px-3 px-md-4 px-lg-5 d-flex align-items-stretch">
          <div className="flex-fill">
            <div className="py-4">
              {initialFiltersReady && (
                <GenericGrid
                  gridId="eligibility-grid"
                  colDefs={colDefs}
                  dataProvider={loadData}
                  filters={filters}
                  key={gridReloadKey}
                />
              )}
            </div>
          </div>
        </div>
      </main>

      <Modal
        show={commentsModalShow}
        onHide={() => setCommentsModalShow(false)}
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <>
          <Modal.Header closeButton>
            <Modal.Title id="contained-modal-title-vcenter">
              Status Notes
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="d-flex flex-column gap-3">
              {comments &&
                [...comments].reverse().map((comment, index: number) => (
                  <>
                    <div key={index} className="border rounded px-3 py-2">
                      <strong>
                        {workflowStatesCache.get(comment.statusId)}
                      </strong>
                      {comment.comments && (
                        <div className="d-flex align-items-start py-1 fs-14">
                          <div>
                            <span className="material-symbols-outlined fs-20 me-1 mt-1 text-light">
                              chat
                            </span>
                          </div>
                          <div className="flex-fill break-word mb-1">
                            {String(comment.comments || "")}
                          </div>
                        </div>
                      )}

                      <div className="d-flex justify-content-between fs-12">
                        <div className="text-light">
                          {formatDateTime(comment.createdAt)}
                        </div>
                        <div className="d-flex align-items-center text-base">
                          <span className="material-symbols-outlined fs-18 me-1">
                            person
                          </span>
                          <span>
                            <UserName userId={comment.createdBy} />
                          </span>
                        </div>
                      </div>
                    </div>
                  </>
                ))}
            </div>
          </Modal.Body>
        </>
      </Modal>

      <OffCanvas
        title={isEnrollPatient ? "Enroll Patient" : "Edit Eligible Patient"}
        offCanvasShow={offCanvasShow}
        setOffCanvasShow={setOffCanvasShow}
        placement="top"
        backdrop="static"
        className="lightbox-offcanvas eligible-form-offcanvas"
      >
        {isEnrollPatient ? (
          <AddPatient
            eligiblePatient={selectedEligible}
            onSubmitPatient={showToaster}
            setOffCanvasShow={setOffCanvasShow}
            reloadGrid={reloadGrid}
          />
        ) : (
          <EditEligiblePatient
            eligiblePatient={selectedEligible}
            handleEnrollPatient={handleEnrollPatient}
            setOffCanvasShow={setOffCanvasShow}
            showToaster={showToaster}
            reloadGrid={reloadGrid}
          />
        )}
      </OffCanvas>
    </>
  );
};
